import { Info, Shuffle } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  EditableThemeTokenName,
  EditableThemeTokensByVariant,
  ThemeVariant,
} from '@/shared/theme/theme-config'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { SheetFooter } from '@/shared/ui/sheet'
import {
  defaultThemePreset,
  editableThemeTokens,
  themePresets,
} from '@/shared/theme/theme-config'
import { cn } from '@/shared/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { ColorPickerDropdown } from '@/features/theme/ui/color-picker-dropdown'
import { tokenUsageExamples } from '@/features/theme/ui/token-usage-examples'

const tokenLabels: Record<EditableThemeTokenName, string> = {
  background: 'Background',
  foreground: 'Foreground',
  card: 'Card',
  'card-foreground': 'Card Foreground',
  primary: 'Primary',
  'primary-foreground': 'Primary Foreground',
  accent: 'Accent',
  'accent-foreground': 'Accent Foreground',
  border: 'Border',
  ring: 'Ring',
  sidebar: 'Sidebar',
  'sidebar-foreground': 'Sidebar Foreground',
  'sidebar-primary': 'Sidebar Primary',
  'sidebar-primary-foreground': 'Sidebar Primary Foreground',
  'sidebar-accent': 'Sidebar Accent',
  'sidebar-accent-foreground': 'Sidebar Accent Foreground',
}

function ThemeTokenUsageDropdown({ token }: { token: EditableThemeTokenName }) {
  const { t } = useTranslation()
  const usage = tokenUsageExamples[token]

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label={t('theme:whereUsedAria', { token: tokenLabels[token] })}
        >
          <Info className="size-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80 p-0">
        <div className="px-3 py-2">
          <DropdownMenuLabel className="px-0">
            {tokenLabels[token]}
          </DropdownMenuLabel>
          <p className="text-sm text-muted-foreground">{usage.summary}</p>
          <ul className="mt-2 list-disc pl-4 text-sm text-muted-foreground">
            {usage.examples.map((example) => (
              <li key={example}>{example}</li>
            ))}
          </ul>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function buildSwatches(variant: ThemeVariant, token: EditableThemeTokenName) {
  return [
    {
      label: 'Monochrome',
      value: themePresets.current[variant][token],
    },
    {
      label: 'Default',
      value: defaultThemePreset[variant][token],
    },
  ].filter((swatch, index, all) => {
    return all.findIndex((item) => item.value === swatch.value) === index
  })
}

function ThemeTokenRow({
  variant,
  token,
  value,
  onChange,
}: {
  variant: ThemeVariant
  token: EditableThemeTokenName
  value: string
  onChange: (value: string) => void
}) {
  const swatches = useMemo(
    () => buildSwatches(variant, token),
    [token, variant],
  )

  return (
    <div className="rounded-xl border border-border/70 bg-card/70 p-3 shadow-xs">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-1">
            <Label htmlFor={`${variant}-${token}`}>{tokenLabels[token]}</Label>
            <ThemeTokenUsageDropdown token={token} />
          </div>
          <div className="mt-1 text-xs text-muted-foreground">{token}</div>
        </div>
        <div
          className="h-8 w-8 rounded-md border border-border shadow-inner"
          style={{ backgroundColor: value }}
          aria-hidden="true"
        />
      </div>

      <div className="space-y-3">
        <Input
          id={`${variant}-${token}`}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="oklch(...) or #rrggbb"
        />

        <ColorPickerDropdown
          value={value}
          onChange={onChange}
          variant={variant}
          token={token}
          appSwatches={swatches}
        />
      </div>
    </div>
  )
}

function ThemeVariantSection({
  variant,
  tokens,
  onTokenChange,
}: {
  variant: ThemeVariant
  tokens: EditableThemeTokensByVariant[ThemeVariant]
  onTokenChange: (token: EditableThemeTokenName, value: string) => void
}) {
  return (
    <section className="space-y-4">
      <div>
        <p className="mt-1 text-sm text-muted-foreground">
          Adjust core surfaces, content contrast, accents, and sidebar colors.
        </p>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {editableThemeTokens.map((token) => (
          <ThemeTokenRow
            key={`${variant}-${token}`}
            variant={variant}
            token={token}
            value={tokens[token]}
            onChange={(value) => onTokenChange(token, value)}
          />
        ))}
      </div>
    </section>
  )
}

export function CustomThemeActions({
  canConfigure,
  onRandomize,
  onReset,
}: {
  canConfigure: boolean
  onRandomize: () => void
  onReset: () => void
}) {
  const { t } = useTranslation()

  if (!canConfigure) {
    return null
  }

  return (
    <div className="mb-4 mt-2 flex flex-wrap items-center gap-2">
      <Button type="button" variant="secondary" onClick={onRandomize}>
        <Shuffle className="size-4" />
        {t('theme:randomize')}
      </Button>
      <Button type="button" variant="outline" onClick={onReset}>
        {t('theme:resetBranding')}
      </Button>
    </div>
  )
}

export function CustomThemeError({ error }: { error: string | null }) {
  if (!error) {
    return null
  }

  return (
    <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
      {error}
    </div>
  )
}

const themeVariants: Array<{ value: ThemeVariant; label: string }> = [
  { value: 'light', label: 'Light Theme' },
  { value: 'dark', label: 'Dark Theme' },
]

export function CustomThemeEditor({
  draft,
  onTokenChange,
}: {
  draft: EditableThemeTokensByVariant
  onTokenChange: (
    variant: ThemeVariant,
    token: EditableThemeTokenName,
    value: string,
  ) => void
}) {
  const { t } = useTranslation()
  const [activeVariant, setActiveVariant] = useState<ThemeVariant>('light')

  const variantLabelMap: Record<ThemeVariant, string> = {
    light: t('theme:lightTheme'),
    dark: t('theme:darkTheme'),
  }

  return (
    <div className="space-y-8">
      <div
        role="tablist"
        aria-label="Theme variant"
        className="inline-flex justify-start border-b border-border"
      >
        {themeVariants.map((variant) => (
          <button
            key={variant.value}
            type="button"
            role="tab"
            aria-selected={activeVariant === variant.value}
            className={cn(
              '-mb-px min-h-10 border-b-2 px-1 py-2 text-sm font-medium transition-colors sm:px-4',
              activeVariant === variant.value
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
            )}
            onClick={() => setActiveVariant(variant.value)}
          >
            {variantLabelMap[variant.value]}
          </button>
        ))}
      </div>
      <ThemeVariantSection
        variant={activeVariant}
        tokens={draft[activeVariant]}
        onTokenChange={(token, value) =>
          onTokenChange(activeVariant, token, value)
        }
      />
    </div>
  )
}

export function CustomThemeFooter({
  canConfigure,
  isSaving,
  onCancel,
  onSave,
}: {
  canConfigure: boolean
  isSaving: boolean
  onCancel: () => void
  onSave: () => void
}) {
  const { t } = useTranslation()

  return (
    <SheetFooter className="border-t">
      <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common:cancel')}
        </Button>
        {canConfigure ? (
          <Button type="button" onClick={onSave} disabled={isSaving}>
            {t('common:save')}
          </Button>
        ) : null}
      </div>
    </SheetFooter>
  )
}
